import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, SimpleChanges } from '@angular/core';
import { NavigationService } from '../services/navigation.service';
import { UUIDHelper } from '@nts/std/utility';
import { NavigationFieldConfigurationInterface } from './navigation-field.directive';

export enum FocusFieldStrategy {
    First = 'first',
    Last = 'last',
    None = 'none',
    RememberLastField = 'rememberLastField'
}

export interface NavigationSectionConfigurationInterface {
    sectionId: string;
    isToolbar: boolean;
    rememberLastFieldFocus?: boolean;
    navigationSectionOrder?: number | null;
    element: ElementRef;
    containerId?: string;
    blur(ignoreRemmeberLastFieldFocus?: boolean): void;
    onFocus(focusFieldStrategy?: FocusFieldStrategy): void;
    hasLastFocusedField: boolean;
}

@Directive({
    selector: '[ntsNavigationSection]',
    standalone: true,
})
export class NavigationSectionDirective implements OnDestroy, OnInit, NavigationSectionConfigurationInterface {
    // Handler per prevenire lo scroll automatico con frecce
    onKeyDown(event: KeyboardEvent) {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
        }
    }

    /**
     * * If true, the section will be registered in the navigation service.
     * * If false, the section will not be registered in the navigation service.
     */
    @Input() ntsNavigationSection: boolean = true;

    /**
     * * If true, save the last position of the field.
     */
    @Input() rememberLastFieldFocus?: boolean = false;

    /**
     * * If true, save the last position of the section in the navigation service.
     * * If false, do not save the last position of the section in the navigation service.
     */
    @Input() navigationSectionOrder?: number = null;

    /**
     * * Unique ID of the section.
     * * If not provided, a new UUID will be generated.
     */
    @Input() sectionId: string = UUIDHelper.generateUUID();

    /**
     * * If true, the section is a toolbar.
     * * If false, the section is not a toolbar.
     * * Default is false.
     */
    @Input() isToolbar: boolean = false;

    @Output() onNavigationSectionBlur = new EventEmitter();

    /**
     * Se rememberLastPosition è true, salva l'ultimo campo attivo.
     */
    lastFocusedField?: NavigationFieldConfigurationInterface | null;

    firstRegistration = false;

    /**
     * * Unique ID of the container.
     */
    containerId?: string;

    get hasLastFocusedField(): boolean {
        return this.lastFocusedField !== null && this.lastFocusedField !== undefined && this.navigationService.fieldsFromActiveContainer.some(field => field.fieldId === this.lastFocusedField.fieldId);
    }

    constructor(
        private navigationService: NavigationService,
        public element: ElementRef,
        private renderer: Renderer2
    ) {

    }

    ngOnInit(): void {
        if (this.ntsNavigationSection === true && this.firstRegistration === false) {
            this.firstRegistration = true;
            this.navigationService.registerSection(this);
        }
        // Aggiungo handler keydown al container
        this.element.nativeElement.addEventListener('keydown', this.onKeyDown.bind(this));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ntsNavigationSection']) {
            if (changes['ntsNavigationSection'].currentValue !== changes['ntsNavigationSection'].previousValue || !this.firstRegistration) {
                if (this.ntsNavigationSection === true) {
                    this.firstRegistration = true;
                    this.navigationService.registerSection(this);
                } else {
                    this.navigationService.unregisterSection(this);
                }
            }
        } else if (changes['sectionId']) {
            if (!changes['sectionId'].firstChange) {
                if (changes['sectionId'].currentValue !== changes['sectionId'].previousValue) {
                    this.navigationService.unregisterSection({ ...this, sectionId: changes['sectionId'].previousValue });
                    this.navigationService.registerSection(this);
                }
            }
        }
    }

    ngOnDestroy(): void {
        if (this.ntsNavigationSection === true) {
            this.navigationService.unregisterSection(this);
        }
    }

    blur(ignoreRemmeberLastFieldFocus = false): void {
        if (this.rememberLastFieldFocus === true && ignoreRemmeberLastFieldFocus === false) {
            this.lastFocusedField = this.navigationService.activeField;
        } else {
            this.lastFocusedField = null;
        }

        this.onNavigationSectionBlur.emit();

        if (this.navigationService.activeSection?.sectionId === this.sectionId) {
            this.navigationService.setActiveSection(null);
        }

        if (this.navigationService.activeField) {
            this.navigationService.activeField.blur();
        }
    }

    onFocus(
        focusFieldStrategy: FocusFieldStrategy = FocusFieldStrategy.RememberLastField
    ): void {
        this.navigationService.setActiveSection(this.sectionId);

        if (focusFieldStrategy === FocusFieldStrategy.RememberLastField) {
            if (this.lastFocusedField?.fieldId) {
                this.navigationService.setActiveField(this.lastFocusedField.fieldId);
            } else {
                if (this.navigationService.fieldsFromActiveSection && this.navigationService.fieldsFromActiveSection[0]) {
                    this.navigationService.setActiveField(
                        this.navigationService.fieldsFromActiveSection[0].fieldId
                    );
                }
            }
        } else if (focusFieldStrategy === FocusFieldStrategy.Last) {
            this.navigationService.setActiveField(
                this.navigationService.fieldsFromActiveSection[this.navigationService.fieldsFromActiveSection.length - 1].fieldId
            );
        } else if (focusFieldStrategy === FocusFieldStrategy.First) {
            if (this.navigationService.fieldsFromActiveSection && this.navigationService.fieldsFromActiveSection[0]) {
                this.navigationService.setActiveField(
                    this.navigationService.fieldsFromActiveSection[0].fieldId
                );
            }
        }
    }
}
